/** @format */

import { LocaleText } from "../../../features/i18n/init";
import {
  Card,
  CardObject,
  SublessonResponse,
  UuidString,
} from "../../../features/resources/types";
import { store } from "../../store";
import { api } from "../api";

export type ResourceCardBody = {
  Object: CardObject;
  PK: string;
  SK: string;
  Parent: string;
  UUID: string;
  Code: string;
};

export const cardsApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * getResourceSubLessonCards query (GET)
     * @param {string} resourceId - the resource's id.
     * @param {string} subLessonUuid - the parent sublesson of cards.
     * @param {string[] | undefined} cardUuids - an optional field to only fetch cards specified.
     * @returns {Card[]} cards related to the sublesson.
     */
    getResourceSubLessonCards: build.query<
      Card[],
      { resourceId: string; subLessonUuid: string; cardUuids?: string[] }
    >({
      query: (args) => {
        const { resourceId, subLessonUuid, cardUuids } = args;
        return {
          url: `resource/${resourceId}/${subLessonUuid}${
            cardUuids ? `?cards=${cardUuids.join(",")}` : ""
          }`,
        };
      },
      transformResponse(res: SublessonResponse) {
        res.cards.forEach((card) => {
          /*  
            If the card is type True / False and is not a multidimensional array.
            convert the legacy cms version to the new format before sending it to the component. 
            If the card is edited the new version is saved. 
          */
          if (
            card.Object.type === "trueFalse" &&
            Array.isArray(card.Object.data.activityContent[0]) === true
          ) {
            /* console.log("legacy t/f card"); */
            const activity = card.Object.data.activityContent.map(
              (item: { str: LocaleText; correct?: boolean }[]) => ({ ...item }),
            );
            card.Object.data.activityContent = activity;
            return card.Object;
          }
        });
        return res.cards;
      },
      providesTags: (result, err, { subLessonUuid }) =>
        result
          ? [
              ...result.map(({ UUID }) => ({
                type: "Card" as const,
                id: UUID,
              })),
              { type: "Card", id: `LIST-${subLessonUuid}` },
            ]
          : [{ type: "Card", id: "LIST" }],
    }),

    /**
     * getResourceCards query (GET)
     * @param {string} resourceId - the resource's id.
     * @returns {Card[]} all cards associated with the resource.
     */
    getResourceCards: build.query<Card[], { resourceId: string }>({
      query: (args) => {
        const { resourceId } = args;

        if (store.getState().auth.userToken?.token) {
          return `resource/auth/${resourceId}/cards`;
        }
        return `resource/${resourceId}/cards`;
      },
      transformResponse: (response: { cards: Card[] }) => {
        return response.cards;
      },
    }),

    /**
     * saveResourceCard mutation (POST)
     * @param {ResourceCardBody} resourceCardItem - the new card item object.
     * @returns the success message
     */
    saveResourceCard: build.mutation<
      { message: string },
      { resourceCardItem: ResourceCardBody }
    >({
      query(body) {
        return {
          url: "resource/save-card",
          method: "POST",
          body,
          credentials: "include",
        };
      },
      invalidatesTags: (_, __, { resourceCardItem }) => [
        { type: "Card", id: `LIST-${resourceCardItem.Parent}` },
      ],
    }),

    /**
     * saveResourceCards mutation (POST)
     * @param {ResourceCardBody[]} resourceCardItems - an array of new card item objects to replace their existing counterpart.
     * @returns {string[]} a list of update messages.
     */
    saveResourceCards: build.mutation<
      string[],
      { resourceCardItems: ResourceCardBody[] }
    >({
      query(body) {
        return {
          url: "resource/save-cards",
          method: "POST",
          body,
          credentials: "include",
        };
      },
      transformResponse: (response: { messages: string[] }) =>
        response.messages,
    }),

    /**
     * deleteResourceCard mutation (DELETE)
     * @param {string} code - Resource attached to card
     * @param {UuidString} subLessonUuid - Card's parent subLesson UUID
     * @param {UuidString} cardUuid - Card's UUID
     * @returns {string} a response if successful.
     */
    deleteResourceCard: build.mutation<
      string,
      { code: string; subLessonUuid: UuidString; cardUuid: UuidString }
    >({
      query: (args) => {
        const { code, subLessonUuid, cardUuid } = args;

        return {
          url: `resource/delete-card/${code}/${subLessonUuid}/${cardUuid}`,
          method: "DELETE",
        };
      },
      transformResponse: (response: { message: string }) => response.message,
      invalidatesTags: (res, err, { subLessonUuid }) => [
        { type: "Card", id: `LIST-${subLessonUuid}` },
        { type: "SubLesson", id: subLessonUuid },
        "Resource", // TODO: REMOVE AT LATER DATE
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetResourceSubLessonCardsQuery,
  useGetResourceCardsQuery,
  useSaveResourceCardMutation,
  useSaveResourceCardsMutation,
  useDeleteResourceCardMutation,
} = cardsApi;
